import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { splitHttpUri } from '~/attendee/helpers/url/split-http-uri.helper';
import { CmsSeo } from '~/cms/interfaces/seo.cms.interface';
import { ACTIVITIES_BASE_URL } from '~/core/core.module';

import { MicrodataTypeEnum } from './microdata.enum';

interface BreadcrumbItem {
  item: string;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class CustomHeadTagService {
  private renderer: Renderer2;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(ACTIVITIES_BASE_URL) private readonly baseUrl: string,
    private readonly rendererFactory: RendererFactory2,
    private readonly router: Router,
    private readonly meta: Meta
  ) {}

  public addSeoTags(cmsSeo: CmsSeo): void {
    if (!cmsSeo) {
      return;
    }

    const { link: linkCollection, html, rdfa } = cmsSeo;
    if (linkCollection?.length) {
      linkCollection?.forEach((seoLink) => {
        this.addTag('link', {
          ...(seoLink.rel && { rel: seoLink.rel }),
          ...(seoLink.href && { href: seoLink.href }),
          ...(seoLink.hreflang && { hreflang: seoLink.hreflang }),
        });
      });
    }
    for (const tag of [...html, ...rdfa]) {
      delete tag.id;
      this.meta.updateTag(tag);
    }
  }

  public addTag(
    tag: string,
    properties: { [key: string]: string },
    content?: string
  ): void {
    this.renderer = this.rendererFactory.createRenderer(null, null);

    const customHeadTag = this.renderer.createElement(tag);

    Object.keys(properties).forEach((key) => {
      this.renderer.setAttribute(customHeadTag, key, properties[key]);
    });

    if (content) {
      customHeadTag.text = content;
    }

    this.renderer.appendChild(this.document.head, customHeadTag);
  }

  public destroyMicrodata(): void {
    // Destroy existing microdata when you left the page
    const microdata = this.document.querySelectorAll('[id^="microdata"]');

    if (microdata) {
      microdata.forEach((script) => script.parentNode.removeChild(script));
    }
  }

  public newBreadcrumItem(name: string, absoluteUrl?: string): BreadcrumbItem {
    return {
      name,
      item:
        absoluteUrl || `${this.baseUrl}${splitHttpUri(this.router.url).uri}`,
    };
  }

  /**
   * @param tagId Unique ID of the tag to remove from the DOM
   */
  public removeTag(tagId: string): void {
    const tag = this.document.getElementById(tagId);

    if (!tag) {
      return;
    }

    tag.parentNode.removeChild(tag);
  }

  public setBreadcrumb(items: BreadcrumbItem[]): void {
    const itemListElement = items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      ...item,
    }));

    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement,
    };

    this.setMicrodata(breadcrumb, MicrodataTypeEnum.Breadcrumb);
  }

  public setMicrodata(data: object, type: MicrodataTypeEnum): void {
    // Add script tag & assign an unique id (1 type allowed by page)
    if (!this.document.getElementById(`microdata-${type}`)) {
      this.addTag(
        'script',
        { type: 'application/ld+json', id: `microdata-${type}` },
        JSON.stringify(data)
      );
    }
  }
}
